import { createMutationAtom } from 'atoms/create-resource-atom';
import searchAtomPair, { SearchParams } from 'atoms/search-atom';
import { FailedApiResponse } from 'components/well-page-content/types';
import { atom } from 'jotai';
import request from 'lib/request';
import { Resource } from 'types/api-responses';
import { AreaConfigs, GlobalConfig, ProductionStreamConfigData } from './types';
import {
  AreaConfigResponse,
  DcaScenarioResponse,
  GeneralConfig,
  OutlierConfig,
  SegmentationConfig,
  ValidateAreaConfigResponse,
} from 'types/dca-scenarios-api/responses';
import { sanitizePayload, sanitizeProductionStreamConfig } from './utils';

interface AreaGeometryResult {
  id: number;
  href: string;
  name: string;
  displayName: string;
  category: string;
  grossAcres: number;
  shared: boolean;
  active: boolean;
  createdBy: string;
  dataSource: string;
  purpose: string;
  createdAt: string;
  updatedAt: string;
  resources: {
    comments: Resource;
    events: Resource;
  };
}

interface AreaGeometryFormSearchParams extends SearchParams {
  text?: string;
  category?: string;
  active?: boolean;
  shared?: boolean;
}

const createAreaGeometryCategoriesAtom = () => {
  return searchAtomPair<AreaGeometryResult, AreaGeometryFormSearchParams>(
    'areaGeometries',
    { page: 1, pageSize: 1 }
  );
};

const createAreaGeometryDetailsSearchAtom = () => {
  return searchAtomPair<AreaGeometryResult, AreaGeometryFormSearchParams>(
    'areaGeometries'
  );
};

const saveGeneralConfigAtom = createMutationAtom<
  GeneralConfig,
  'createDcaScenarioGeneralConfig'
>();
const saveOutlierConfigAtom = createMutationAtom<
  OutlierConfig,
  'createDcaScenarioOutlierConfig'
>();
const saveSegmentationConfigAtom = createMutationAtom<
  SegmentationConfig,
  'createDcaScenarioSegmentationConfig'
>();

const validateAreaConfigAtom = createMutationAtom<
  ValidateAreaConfigResponse,
  'validateAreaConfig'
>();

interface UpdateDcaScenarioAtomType {
  loading: boolean;
  error: string | null;
}

const dcaScenarioDataAtom = atom<UpdateDcaScenarioAtomType>({
  loading: false,
  error: null,
});

interface SaveDcaScenarioPayload {
  payload: {
    globalConfig: GlobalConfig;
    areaConfig: AreaConfigs;
    generalConfigId: string;
    segmentationConfigId: string;
    outlierConfigId: string;
    productionStreamConfigs: ProductionStreamConfigData[];
  };
  onSuccess?: (data: {
    dcaScenarioId: string;
    areaConfig: AreaConfigResponse;
  }) => void;
}

const saveDcaScenarioAtom = atom(
  (get) => get(dcaScenarioDataAtom),
  async (get, set, data: SaveDcaScenarioPayload) => {
    set(dcaScenarioDataAtom, {
      loading: true,
      error: null,
    });

    const payload = data.payload;

    try {
      const newScenarioResponse = await request.post<any, FailedApiResponse>(
        `/dca_scenarios`,
        {
          dcaScenario: {
            name: payload.globalConfig.name,
            notes: payload.globalConfig.notes,
            timeFormat: payload.globalConfig.timeFormat,
            qiFormat: payload.globalConfig.qiFormat,
            diFormat: payload.globalConfig.diFormat,
            durationOption: payload.globalConfig.durationOption,
            durationFormat: payload.globalConfig.durationFormat,
            durationPeriod: payload.globalConfig.durationPeriod,
            isProtected: false,
          },
        },
        new AbortController()
      );

      if (newScenarioResponse.type === 'error') {
        const errors = newScenarioResponse.data?.errors;
        let msg = '';
        errors?.forEach((error) => {
          msg += `${error.message}\n`;
        });

        const { details } = errors?.[0] || {};

        if (details && typeof details === 'object') {
          const detailMsg = Object.entries(details)
            .map(([key, value]) => `${key}: ${value}`)
            .join('\n');
          if (detailMsg) {
            msg = detailMsg;
          }
        }

        throw new Error(msg);
      } else if (newScenarioResponse.type !== 'success') {
        throw new Error('Error creating scenario');
      }

      const scenarioId = newScenarioResponse.data.id;

      const newAreaConfigResponse = await request.post<any, FailedApiResponse>(
        `/dca_scenarios/${scenarioId}/area_configs`,
        {
          developmentType: payload.areaConfig.developmentType,
          areaGeometryId: payload.areaConfig.areaGeometryId,
          formation: payload.areaConfig.formation,
          dcaScenarioGeneralConfigId: payload.generalConfigId,
          dcaScenarioSegmentationConfigId: payload.segmentationConfigId,
          dcaScenarioOutlierConfigId: payload.outlierConfigId,
        },
        new AbortController()
      );

      if (newAreaConfigResponse.type === 'error') {
        const errors = newAreaConfigResponse.data?.errors;
        let msg = '';
        errors?.forEach((error) => {
          msg += `${error.message}\n`;
        });

        const { details } = errors?.[0] || {};

        if (details && typeof details === 'object') {
          const detailMsg = Object.entries(details)
            .map(([key, value]) => `${key}: ${value}`)
            .join('\n');
          if (detailMsg) {
            msg = detailMsg;
          }
        }

        throw new Error(msg);
      } else if (newAreaConfigResponse.type !== 'success') {
        throw new Error('Error creating scenario');
      }

      const areaConfigId = newAreaConfigResponse.data.id;

      const promises = payload.productionStreamConfigs.map((config) => {
        return request.post<any, FailedApiResponse>(
          `/dca_scenarios/${scenarioId}/area_configs/${areaConfigId}/production_stream_configs`,
          sanitizeProductionStreamConfig(config),
          new AbortController()
        );
      });

      await Promise.allSettled(promises);

      set(dcaScenarioDataAtom, {
        loading: false,
        error: null,
      });

      data?.onSuccess?.({
        dcaScenarioId: scenarioId,
        areaConfig: newAreaConfigResponse.data,
      });
    } catch (error) {
      set(dcaScenarioDataAtom, {
        loading: false,
        error:
          error instanceof Error ? error.message : 'Error creating scenario',
      });
    }
  }
);

interface UpdateDcaScenarioPayload {
  payload: {
    dcaScenarioId: string;
    areaConfigId: string;
    globalConfig: Partial<GlobalConfig> | null;
    areaConfig: Partial<AreaConfigs> | null;
    generalConfigId: string | null;
    segmentationConfigId: string | null;
    outlierConfigId: string | null;
    productionStreamConfigs: Partial<ProductionStreamConfigData>[];
  };
  onSuccess?: (data: {
    areaConfig: AreaConfigResponse | null;
    productionStreamConfigs: ProductionStreamConfigData[];
  }) => void;
}

const updateDcaScenarioAtom = atom(
  (get) => get(dcaScenarioDataAtom),
  async (get, set, data: UpdateDcaScenarioPayload) => {
    set(dcaScenarioDataAtom, { loading: true, error: null });
    const payload = data.payload;

    try {
      if (Object.keys(payload.globalConfig || {}).length > 0) {
        await request.patch<any, FailedApiResponse>(
          `/dca_scenarios/${payload.dcaScenarioId}`,
          {
            dcaScenario: payload.globalConfig,
          },
          new AbortController()
        );
      }

      let areaConfigPayload: any = {};

      if (Object.keys(payload.areaConfig || {}).length > 0) {
        areaConfigPayload = payload.areaConfig;
      }

      if (payload.generalConfigId) {
        areaConfigPayload.dcaScenarioGeneralConfigId = payload.generalConfigId;
      }

      if (payload.segmentationConfigId) {
        areaConfigPayload.dcaScenarioSegmentationConfigId =
          payload.segmentationConfigId;
      }

      if (payload.outlierConfigId) {
        areaConfigPayload.dcaScenarioOutlierConfigId = payload.outlierConfigId;
      }

      let areaConfigResponse: AreaConfigResponse | null = null;

      if (Object.keys(areaConfigPayload).length > 0) {
        const response = await request.patch<
          AreaConfigResponse,
          FailedApiResponse
        >(
          `/dca_scenarios/${payload.dcaScenarioId}/area_configs/${payload.areaConfigId}`,
          areaConfigPayload,
          new AbortController()
        );

        if (response.type === 'success') {
          areaConfigResponse = response.data;
        }
      }

      const changedConfigs = payload.productionStreamConfigs.filter(
        (config) => Object.keys(config).length > 0
      );

      let productionStreamConfigResults: ProductionStreamConfigData[] = [];

      if (changedConfigs.length > 0) {
        const promises: Promise<any>[] = [];

        changedConfigs.forEach(({ id, ...config }) => {
          if (!id) {
            promises.push(
              request.post<ProductionStreamConfigData, FailedApiResponse>(
                `/dca_scenarios/${payload.dcaScenarioId}/area_configs/${payload.areaConfigId}/production_stream_configs`,
                sanitizeProductionStreamConfig(
                  config as ProductionStreamConfigData
                ),
                new AbortController()
              )
            );
          } else {
            promises.push(
              request.patch<ProductionStreamConfigData, FailedApiResponse>(
                `/dca_scenarios/${payload.dcaScenarioId}/area_configs/${payload.areaConfigId}/production_stream_configs/${id}`,
                sanitizePayload(config),
                new AbortController()
              )
            );
          }
        });

        await Promise.allSettled(promises);

        const productionStreamConfigsResponse = await request.get<{
          results: ProductionStreamConfigData[];
          totalCount: number;
        }>(
          `/dca_scenarios/${payload.dcaScenarioId}/area_configs/${payload.areaConfigId}/production_stream_configs`,
          new AbortController()
        );

        if ('error' in productionStreamConfigsResponse) {
          throw new Error('Error fetching production stream configs');
        }

        productionStreamConfigResults = productionStreamConfigsResponse.results;
      }

      set(dcaScenarioDataAtom, {
        loading: false,
        error: null,
      });

      data?.onSuccess?.({
        areaConfig: areaConfigResponse,
        productionStreamConfigs: productionStreamConfigResults,
      });
    } catch (error) {
      set(dcaScenarioDataAtom, {
        loading: false,
        error: error as any,
      });
    }
  }
);

interface SaveAreaConfigPayload {
  payload: SaveDcaScenarioPayload['payload'] & {
    dcaScenarioId: string;
  };
  onSuccess?: (data: {
    dcaScenarioId: string;
    areaConfig: AreaConfigResponse;
    productionStreamConfigs: ProductionStreamConfigData[];
  }) => void;
}

const saveAreaConfigAtom = atom(
  (get) => get(dcaScenarioDataAtom),
  async (get, set, data: SaveAreaConfigPayload) => {
    set(dcaScenarioDataAtom, { loading: true, error: null });

    try {
      const payload = data.payload;
      const scenarioId = payload.dcaScenarioId;

      const newAreaConfigResponse = await request.post<any, FailedApiResponse>(
        `/dca_scenarios/${scenarioId}/area_configs`,
        {
          developmentType: payload.areaConfig.developmentType,
          areaGeometryId: payload.areaConfig.areaGeometryId,
          formation: payload.areaConfig.formation,
          dcaScenarioGeneralConfigId: payload.generalConfigId,
          dcaScenarioSegmentationConfigId: payload.segmentationConfigId,
          dcaScenarioOutlierConfigId: payload.outlierConfigId,
        },
        new AbortController()
      );

      if (newAreaConfigResponse.type === 'error') {
        const errors = newAreaConfigResponse.data?.errors;
        let msg = '';
        errors?.forEach((error) => {
          msg += `${error.message}\n`;
        });

        const { details } = errors?.[0] || {};

        if (details && typeof details === 'object') {
          const detailMsg = Object.entries(details)
            .map(([key, value]) => `${key}: ${value}`)
            .join('\n');
          if (detailMsg) {
            msg = detailMsg;
          }
        }

        throw new Error(msg);
      } else if (newAreaConfigResponse.type !== 'success') {
        throw new Error('Error creating scenario');
      }

      const areaConfigId = newAreaConfigResponse.data.id;

      const promises = payload.productionStreamConfigs.map((config) => {
        return request.post<any, FailedApiResponse>(
          `/dca_scenarios/${scenarioId}/area_configs/${areaConfigId}/production_stream_configs`,
          sanitizeProductionStreamConfig(config),
          new AbortController()
        );
      });

      await Promise.allSettled(promises);

      let productionStreamConfigResults: ProductionStreamConfigData[] = [];

      const productionStreamConfigsResponse = await request.get<{
        results: ProductionStreamConfigData[];
        totalCount: number;
      }>(
        `/dca_scenarios/${scenarioId}/area_configs/${areaConfigId}/production_stream_configs`,
        new AbortController()
      );

      if ('error' in productionStreamConfigsResponse) {
        throw new Error('Error fetching production stream configs');
      }

      productionStreamConfigResults = productionStreamConfigsResponse.results;

      set(dcaScenarioDataAtom, {
        loading: false,
        error: null,
      });

      data?.onSuccess?.({
        dcaScenarioId: scenarioId,
        areaConfig: newAreaConfigResponse.data,
        productionStreamConfigs: productionStreamConfigResults,
      });
    } catch (error) {
      set(dcaScenarioDataAtom, {
        loading: false,
        error:
          error instanceof Error ? error.message : 'Error creating scenario',
      });
    }
  }
);

const deleteProductionStreamConfigAtom = createMutationAtom();
// const deleteAreaConfigAtom = createMutationAtom();

const deleteAreaConfigAtomBase = atom<{
  loading: boolean;
  error: string | null;
}>({ loading: false, error: null });
const deleteAreaConfigAtom = atom(
  (get) => get(deleteAreaConfigAtomBase),
  async (get, set, payload: { url: string; onSuccess?: () => void }) => {
    set(deleteAreaConfigAtomBase, { loading: true, error: null });
    try {
      const response = await request.deleteReq<null, null>(
        payload.url,
        null,
        new AbortController()
      );
      if (response.type === 'success') {
        set(deleteAreaConfigAtomBase, { loading: false, error: null });
        payload?.onSuccess?.();
      }
    } catch (error) {
      set(deleteAreaConfigAtomBase, {
        loading: false,
        error:
          error instanceof Error ? error.message : 'Error deleting area config',
      });
    }
  }
);

const deleteMultipleProductionStreamConfigsAtomBase = atom<{
  loading: boolean;
  error: string | null;
}>({ loading: false, error: null });

interface DeleteMultipleProductionStreamConfigsPayload {
  urls: string[];
  onSuccess?: () => void;
}

const deleteMultipleProductionStreamConfigsAtom = atom(
  (get) => get(deleteMultipleProductionStreamConfigsAtomBase),
  async (get, set, payload: DeleteMultipleProductionStreamConfigsPayload) => {
    set(deleteMultipleProductionStreamConfigsAtomBase, {
      loading: true,
      error: null,
    });

    try {
      const promises = payload.urls.map((url) => {
        return request.deleteReq<null, null>(url, null, new AbortController());
      });
      await Promise.allSettled(promises);
    } catch (error) {
      set(deleteMultipleProductionStreamConfigsAtomBase, {
        loading: false,
        error:
          error instanceof Error
            ? error.message
            : 'Error deleting production stream configs',
      });
    }
    set(deleteMultipleProductionStreamConfigsAtomBase, {
      loading: false,
      error: null,
    });
    payload?.onSuccess?.();
  }
);
const cloneDcaScenarioAtom = createMutationAtom<DcaScenarioResponse, any>();

interface AreaConfigData {
  generalConfig: GeneralConfig | null;
  segmentationConfig: SegmentationConfig | null;
  outlierConfig: OutlierConfig | null;
  productionStreamConfigs: ProductionStreamConfigData[];
}

const areaConfigDataAtomBase = atom<AreaConfigData>({
  generalConfig: null,
  segmentationConfig: null,
  outlierConfig: null,
  productionStreamConfigs: [],
});

interface GeneralConfigData {
  payload: {
    generalConfigUrl: string | null;
    segmentationConfigUrl: string | null;
    outlierConfigUrl: string | null;
    productionStreamConfigsUrl: string | null;
  };
  onSuccess?: (data: AreaConfigData) => void;
}

const areaConfigDataAtom = atom(
  (get) => get(areaConfigDataAtomBase),
  async (get, set, data: GeneralConfigData) => {
    const payload = data.payload;

    const result: AreaConfigData = {
      generalConfig: null,
      segmentationConfig: null,
      outlierConfig: null,
      productionStreamConfigs: [],
    };

    try {
      if (payload.generalConfigUrl) {
        const generalConfigResponse = await request.get<GeneralConfig>(
          payload.generalConfigUrl,
          new AbortController()
        );

        if ('error' in generalConfigResponse) return;

        result.generalConfig = generalConfigResponse;
      }

      if (payload.segmentationConfigUrl) {
        const segmentationConfigResponse =
          await request.get<SegmentationConfig>(
            payload.segmentationConfigUrl,
            new AbortController()
          );

        if ('error' in segmentationConfigResponse) return;

        result.segmentationConfig = segmentationConfigResponse;
      }

      if (payload.outlierConfigUrl) {
        const outlierConfigResponse = await request.get<OutlierConfig>(
          payload.outlierConfigUrl,
          new AbortController()
        );

        if ('error' in outlierConfigResponse) return;

        result.outlierConfig = outlierConfigResponse;
      }

      if (payload.productionStreamConfigsUrl) {
        const productionStreamConfigsResponse = await request.get<{
          results: ProductionStreamConfigData[];
          totalCount: number;
        }>(payload.productionStreamConfigsUrl, new AbortController());

        if ('error' in productionStreamConfigsResponse) return;

        result.productionStreamConfigs =
          productionStreamConfigsResponse.results;
      }

      data.onSuccess?.(result);
    } catch (error) {
      set(areaConfigDataAtomBase, {
        generalConfig: null,
        segmentationConfig: null,
        outlierConfig: null,
        productionStreamConfigs: [],
      });
    }
  }
);

export {
  createAreaGeometryCategoriesAtom,
  createAreaGeometryDetailsSearchAtom,
  saveGeneralConfigAtom,
  saveOutlierConfigAtom,
  saveSegmentationConfigAtom,
  validateAreaConfigAtom,
  updateDcaScenarioAtom,
  saveAreaConfigAtom,
  dcaScenarioDataAtom,
  saveDcaScenarioAtom,
  deleteProductionStreamConfigAtom,
  deleteAreaConfigAtom,
  cloneDcaScenarioAtom,
  AreaGeometryResult,
  AreaGeometryFormSearchParams,
  deleteMultipleProductionStreamConfigsAtom,
  areaConfigDataAtom,
};
